"use client";

import React, { useCallback, useState } from "react";
import Image from "next/image";
import upperFirst from "lodash/upperFirst";
import { ShieldCheck } from "lucide-react";
import { KeyedMutator } from "swr";

import { IInvoice, SubscriptionInvoice } from "@/backend/domains/invoice/domain/Invoice";
import { IPaymentCard } from "@/backend/domains/payment-card/domain/PaymentCard";
import { IPriorityPaymentCard } from "@/backend/domains/payment-card/domain/PriorityPaymentCard";
import { PaymentCalculator } from "@/backend/domains/subscription";
import { ISubscription } from "@/backend/domains/subscription/domain/Subscription";
import { useModal } from "@/client/shared/hooks/useModal";
import { RadioGroup, RadioGroupItem } from "@/client/shared/ui/radio-group";

import { execAction } from "@/lib/executeServerAction";
import { cn } from "@/lib/utils";
import { useHandler } from "@/hooks/useHandler";
import { useServerAction } from "@/hooks/useServerAction";
import { subscriptionStore, useMutateSubscription } from "@/stores/subscription.store";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { DialogContainer } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";
import { clientApi } from "@/actions";
import delay from "@/utils/delay";
import { doIt } from "@/utils/doIt";
import formatCurrency from "@/utils/formatCurrency";
import formatter from "@/utils/formatter";
import { isValidKey } from "@/utils/isValidKey";

import { usePlanActivationStore } from "../..";
import { CheckoutTabProps } from "./CheckoutTab";

//

export interface PaymentCardsTabProps extends CheckoutTabProps {}

//

export const PaymentCardsTab: React.FC<PaymentCardsTabProps> = ({ ...props }) => {
  const tabsModel = usePlanActivationStore((s) => s.tabsModel);

  const { data, mutate: mutateCards } = useServerAction(
    { key: "/payment-cards", organizationId: true },
    clientApi.paymentCards.findCardsWithPriority,
  );

  const openCheckoutHandler = useCallback(
    () => tabsModel.setTab("checkoutTab", props),
    [props, tabsModel],
  );

  if (!data) {
    return (
      <DialogContainer last first className="grid gap-4">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </DialogContainer>
    );
  }

  return data.cards.length === 0 ? (
    <AddingCard openCheckoutHandler={openCheckoutHandler} caption={props.caption} />
  ) : (
    <PaymentCardsList
      openCheckoutHandler={openCheckoutHandler}
      mutateCards={mutateCards}
      {...props}
      invoice={props.invoice}
      {...data}
    />
  );
};

//

interface AddingCardProps {
  openCheckoutHandler: () => void;
  caption?: React.ReactNode;
}

const AddingCard: React.FC<AddingCardProps> = ({ openCheckoutHandler, caption }) => {
  const tabsModel = usePlanActivationStore((s) => s.tabsModel);

  return (
    <DialogContainer last>
      <div className="grid space-y-4">
        <div className="grid space-y-3">
          <Image
            alt="credit-card"
            src={"/images/credit-card.gif"}
            className="justify-self-center"
            width={100}
            height={100}
          />
          <Typography bold h4 className="text-center">
            Добавьте способ оплаты
          </Typography>
          {doIt(() => {
            if (!caption) {
              return (
                <Typography muted className="text-center">
                  Для подтверждения карты мы временно спишем{" "}
                  {formatter.currency(PaymentCalculator.authorizeAmount)}. Сразу после списания эта
                  сумма будет возвращена.
                </Typography>
              );
            }

            if (typeof caption === "string") {
              return (
                <Typography muted className="text-center">
                  {caption}
                </Typography>
              );
            }

            return caption;
          })}
        </div>
        <div className="grid space-y-4">
          <div className="flex gap-3">
            {tabsModel.canBack() && (
              <Button
                variant={"secondary"}
                size={"icon"}
                onClick={() => tabsModel.back()}
                icon={"ArrowLeft"}
              />
            )}
            <Button icon={"CreditCard"} className="flex-1" onClick={openCheckoutHandler}>
              Добавить карту
            </Button>
          </div>
          <div className="text-muted-foreground flex items-center justify-center gap-2">
            <ShieldCheck className="min-w-4" size={16} />
            <Typography sm>Данные защищены и не хранятся у нас.</Typography>
          </div>
        </div>
      </div>
    </DialogContainer>
  );
};

//

type PaymentCardsListProps = {
  cards: IPaymentCard[];
  priorityCard: IPriorityPaymentCard | null;
  openCheckoutHandler: () => void;
  invoice: IInvoice;
  mutateCards: KeyedMutator<{ cards: IPaymentCard[]; priorityCard: IPriorityPaymentCard | null }>;
} & PaymentCardsTabProps;

const PaymentCardsList: React.FC<PaymentCardsListProps> = ({
  cards,
  priorityCard,
  openCheckoutHandler,
  invoice,
  buttonText,
  title,
  caption,
  mutateCards,
}) => {
  const tabsModel = usePlanActivationStore((s) => s.tabsModel);
  const [selectedCardId, setSelectedCardId] = useState(
    priorityCard?.paymentCardId ?? cards.at(0)?.id,
  );
  const mutateSubscription = useMutateSubscription();
  const mutateSubscriptionData = subscriptionStore.useContext((s) => s.mutateSubscriptionData);
  const modal = useModal();

  const payHandler = useHandler(
    async () => {
      if (!selectedCardId) throw new Error("Выберите карту для оплаты.");

      const { Model } = await execAction(() =>
        clientApi.paymentSystem.payByToken(invoice.id, selectedCardId).catch(async (e) => {
          await mutateCards(undefined);
          throw e;
        }),
      );

      const { CardHolderMessage, ReasonCode } = Model;
      if (ReasonCode) throw new Error(CardHolderMessage);

      const createMutateData = async (counter: number = 0): Promise<ISubscription> => {
        const subscription = await execAction(clientApi.subscription.getSubscription);
        if (counter >= 30) return subscription;
        if (subscription.paymentSubscriptionId === Model.SubscriptionId) return subscription;
        await delay(300);
        return await createMutateData(counter + 1);
      };

      await mutateSubscription(() => createMutateData());
      await mutateSubscriptionData((d) =>
        d
          ? { invoice: invoice as IInvoice<SubscriptionInvoice>, paymentPlan: d.paymentPlan }
          : undefined,
      );
      await mutateCards(undefined);

      modal.close();
    },
    { successDescription: "Тариф активирован" },
  );

  return (
    <DialogContainer last>
      <div className="grid gap-4">
        <div className="grid gap-3">
          <Image
            alt="credit-card"
            src={"/images/credit-card.gif"}
            className="justify-self-center"
            width={100}
            height={100}
          />
          <div className="space-y-1.5 text-center">
            <Typography bold h4>
              {title || "Выберите способ оплаты"}
            </Typography>
            {typeof caption === "string" ? <Typography muted>{caption}</Typography> : caption}
          </div>
        </div>
        <div className="grid gap-3">
          <RadioGroup
            className="grid gap-3"
            value={selectedCardId}
            onValueChange={(v) => setSelectedCardId(cards.find((c) => c.id === v)?.id)}
          >
            {cards.map((d) => (
              <PaymentCard
                key={d.id}
                paymentCard={d}
                priorityCardId={priorityCard?.paymentCardId ?? null}
                onClick={() => setSelectedCardId(d.id)}
                isSelected={selectedCardId === d.id}
              />
            ))}
          </RadioGroup>
          <Button variant={"link"} size={"sm"} className="flex-1" onClick={openCheckoutHandler}>
            Оплатить другой картой
          </Button>
        </div>
        <div className="flex gap-3">
          {tabsModel.canBack() && (
            <Button
              variant={"secondary"}
              size={"icon"}
              onClick={() => tabsModel.back()}
              icon={"ArrowLeft"}
            />
          )}
          <Button
            variant={"default"}
            className="flex-1"
            onClick={payHandler.handler}
            loading={payHandler.isLoading}
          >
            {payHandler.isLoading
              ? "Активация тарифа..."
              : buttonText || `Оплатить | ${formatCurrency(invoice.amount)}`}
          </Button>
        </div>
      </div>
    </DialogContainer>
  );
};

//

interface PaymentCardProps {
  className?: string;
  paymentCard: IPaymentCard;
  priorityCardId: string | null;
  isSelected: boolean;
}

const PaymentCard: React.FC<PaymentCardProps & React.ComponentProps<typeof Card>> = ({
  className,
  paymentCard,
  priorityCardId,
  isSelected,
  ...props
}) => {
  const { cardExpDate, cardLastFour, id } = paymentCard;
  const cardType = paymentCard.cardType.toLowerCase();
  const isPriority = priorityCardId ? priorityCardId === id : false;

  const cardTypes: Record<"mastercard" | "visa" | "maestro" | "unknown", string> = {
    maestro: "/images/card-maestro.png",
    visa: "/images/card-visa.png",
    mastercard: "/images/card-mastercard.png",
    unknown: "/images/card-unknown.png",
  };

  const cardTypeImageSrc = isValidKey(cardType, cardTypes)
    ? cardTypes[cardType]
    : cardTypes.unknown;

  return (
    <Card
      className={cn(
        "hover:bg-primary-foreground flex items-center gap-2 overflow-auto p-2 select-none",
        isSelected && "border-primary",
        className,
      )}
      {...props}
    >
      <div className="flex h-9 w-12 items-center justify-center rounded-md border p-2">
        <Image
          alt="payment-card"
          className="object-cover"
          src={cardTypeImageSrc}
          width={48}
          height={48}
        />
      </div>
      <div className="overflow-auto">
        <Typography bold className="flex gap-1.5">
          <span className="truncate">{upperFirst(cardType)}</span>
          <span>*{cardLastFour}</span>
        </Typography>
        <Typography sm muted>
          До {cardExpDate}
        </Typography>
      </div>
      <div className="ml-auto flex items-center gap-2">
        {isPriority && <Badge variant={"secondary"}>Основная</Badge>}
        <RadioGroupItem value={paymentCard.id} />
      </div>
    </Card>
  );
};
